import { useQueries } from "@tanstack/react-query";
import { Radio } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { api, unwrap } from "@/api/client";
import {
  useGenerator,
  useGeneratorReadings,
  useOrganization,
  useSites,
  useTank,
  useTankReadings,
} from "@/api/hooks";
import { qk } from "@/api/keys";
import type { Generator, Tank } from "@/api/types";
import { useActiveOrgId } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  MicroLabel,
  Num,
} from "@/components/ui/primitives";
import { NONE, Select } from "@/components/ui/select";
import { LevelBar, SightGlass } from "@/components/ui/sight-glass";
import { EmptyState, ErrorState, LoadingRows } from "@/components/ui/states";
import { type ChartTone, TrendChart } from "@/components/ui/trend-chart";
import { useI18n } from "@/i18n/provider";
import {
  formatDateTime,
  formatHours,
  formatLiters,
  formatNumber,
  formatRelativeMinutes,
} from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Live Monitoring.
 *
 * Every other screen in this product answers "what happened" — the ledger, the reports.
 * This one answers "what is happening right now", and it reads the same telemetry pipeline
 * a field device pushes into: `GET /v1/tanks/{id}` and `GET /v1/generators/{id}`, polled,
 * and `.../readings` for the trend behind them. There is no separate "live" data source —
 * a customer looking at this screen is looking at the same numbers the ingest API just
 * received.
 *
 * MOST TANKS HERE HAVE NO DEVICE YET, and that is shown honestly rather than hidden: the
 * live pulse only lights up where a reading has actually arrived recently, and a tank or
 * generator with nothing to plot gets an invitation to connect one, not a blank chart.
 */

/** A reading counts as "live" inside this window; older than this and the pulse goes dark. */
const LIVE_WINDOW_MINUTES = 15;

/**
 * Every tank / generator in the org, one request per site — the same N+1 the dashboard and
 * the sites list already run, against the SAME cache keys, so this page shares their cache
 * rather than duplicating the fetch.
 */
function useAllTanks(siteIds: string[]) {
  const orgId = useActiveOrgId();
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: qk.siteTanks(orgId, siteId),
      queryFn: () => unwrap(api.GET("/v1/sites/{siteId}/tanks", { params: { path: { siteId } } })),
    })),
    combine: (results) => ({
      tanks: results.flatMap((r) => r.data?.data ?? []) as Tank[],
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error,
    }),
  });
}

function useAllGenerators(siteIds: string[]) {
  const orgId = useActiveOrgId();
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: qk.siteGenerators(orgId, siteId),
      queryFn: () =>
        unwrap(api.GET("/v1/sites/{siteId}/generators", { params: { path: { siteId } } })),
    })),
    combine: (results) => ({
      generators: results.flatMap((r) => r.data?.data ?? []) as Generator[],
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error,
    }),
  });
}

function ageMinutesSince(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return ms < 0 ? 0 : ms / 60_000;
}

/** The "this is happening right now" mark. Subtle on purpose — DESIGN.md §7. */
function LivePulse() {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-1.5 text-ok" role="status">
      <span className="relative flex size-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ok opacity-60" />
        <span className="relative inline-flex size-2 rounded-full bg-ok" />
      </span>
      <span className="label-caps font-mono text-[10px] font-medium">{t("monitoring.live")}</span>
    </span>
  );
}

/** "Updated 3 minutes ago" / "Never reported" — the one line every tile ends on. */
function ReadingAge({ iso }: { iso: string | undefined }) {
  const { t, lang } = useI18n();
  const minutes = ageMinutesSince(iso);
  if (minutes === null) return <span className="text-[11px] text-faint">{t("monitoring.neverReported")}</span>;
  return (
    <span className="text-[11px] text-faint">
      {t("monitoring.updated", { when: formatRelativeMinutes(lang, minutes) })}
    </span>
  );
}

/* ------------------------------------------------------------ live tiles */

function TankTile({
  tank,
  siteName,
  orgThreshold,
}: {
  tank: Tank;
  siteName: string;
  orgThreshold: number;
}) {
  const { t, lang } = useI18n();
  const liveQuery = useTank(tank.id);
  const live = liveQuery.data ?? tank;

  const percent = live.level_percent ?? null;
  const threshold = live.low_level_threshold_percent ?? orgThreshold;
  const low = percent !== null && percent <= threshold;
  const minutes = ageMinutesSince(live.level_updated_at);
  const isLive = minutes !== null && minutes <= LIVE_WINDOW_MINUTES;
  const level = low ? "low" : "ok";

  return (
    <Card data-testid={`tank-tile-${tank.id}`}>
      <CardBody className="flex items-start gap-3">
        <SightGlass
          percent={percent}
          level={level}
          className="h-16 w-6 shrink-0"
          label={`${tank.name} — ${percent === null ? t("tank.levelUnknown") : `${Math.round(percent)}%`}`}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-text">{tank.name}</p>
            {isLive ? <LivePulse /> : null}
          </div>
          <p className="truncate text-xs text-faint">{siteName}</p>

          <p className="mt-1.5 font-mono text-lg text-text">
            {percent === null ? (
              <span className="text-sm text-faint">{t("tank.levelUnknown")}</span>
            ) : (
              <Num>{formatNumber(lang, Math.round(percent))}%</Num>
            )}
          </p>
          <LevelBar percent={percent} level={level} className="mt-1.5" />

          {live.current_level_liters !== undefined ? (
            <p className="mt-1.5 text-[11px] text-faint">
              <Num>
                {formatLiters(lang, live.current_level_liters)}
                {" / "}
                {formatLiters(lang, tank.capacity_liters)}
              </Num>{" "}
              {t("common.liters")}
            </p>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Badge tone={low ? "low" : "ok"}>{t(low ? "dash.low" : "dash.healthy")}</Badge>
            <ReadingAge iso={live.level_updated_at} />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function GeneratorTile({ generator, siteName }: { generator: Generator; siteName: string }) {
  const { t, lang } = useI18n();
  const liveQuery = useGenerator(generator.id);
  const live = liveQuery.data ?? generator;
  const minutes = ageMinutesSince(live.updated_at);
  const isLive = minutes !== null && minutes <= LIVE_WINDOW_MINUTES;

  return (
    <Card data-testid={`generator-tile-${generator.id}`}>
      <CardBody>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-text">{generator.name}</p>
              {isLive ? <LivePulse /> : null}
            </div>
            <p className="truncate text-xs text-faint">{siteName}</p>
          </div>
          <Badge
            tone={
              generator.status === "active"
                ? "ok"
                : generator.status === "maintenance"
                  ? "low"
                  : "idle"
            }
          >
            {t(`gen.status.${generator.status}` as const)}
          </Badge>
        </div>

        <div className="mt-3 flex items-end justify-between gap-2">
          <div>
            <MicroLabel>{t("gen.runningHours")}</MicroLabel>
            <p className="font-mono text-lg text-text">
              <Num>{formatHours(lang, live.running_hours)}</Num>{" "}
              <span className="text-xs text-faint">{t("common.hours")}</span>
            </p>
          </div>
          <ReadingAge iso={live.updated_at} />
        </div>
      </CardBody>
    </Card>
  );
}

/* ----------------------------------------------------------------- trend */

function ChartFrame({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  // Time always runs left-to-right, oldest to newest, in either language — the chart is a
  // convention of the instrument, not of the sentence around it. `role="img"` carries the
  // accessible summary; the readable min/max/latest captions live OUTSIDE it, in the DOM.
  return (
    <div dir="ltr" role="img" aria-label={label} className="h-24 w-full">
      {children}
    </div>
  );
}

function TankTrendCard({ tank, orgThreshold }: { tank: Tank; orgThreshold: number }) {
  const { t, lang } = useI18n();
  const readingsQuery = useTankReadings(tank.id);

  if (readingsQuery.isPending) return <LoadingRows rows={2} />;
  if (readingsQuery.error) {
    return <ErrorState error={readingsQuery.error} onRetry={() => void readingsQuery.refetch()} />;
  }

  const points = readingsQuery.data?.points ?? [];
  if (points.length === 0) {
    return (
      <EmptyState
        icon={<Radio className="size-6" />}
        title={t("monitoring.noReadingsTitle")}
        body={t("monitoring.noReadingsTankBody")}
      />
    );
  }

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const threshold = tank.low_level_threshold_percent ?? orgThreshold;
  const tone: ChartTone = last.level_percent <= threshold ? "low" : "ok";
  const chartPoints = points.map((p) => ({ x: new Date(p.recorded_at).getTime(), y: p.level_percent }));

  return (
    <div className="flex flex-col gap-3">
      <ChartFrame
        label={`${t("monitoring.tankTrendTitle")} — ${tank.name}, ${t("monitoring.chartSummary", {
          from: `${Math.round(first.level_percent)}%`,
          to: `${Math.round(last.level_percent)}%`,
        })}`}
      >
        <TrendChart points={chartPoints} tone={tone} />
      </ChartFrame>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-faint">
        <Num>{formatDateTime(lang, first.recorded_at)}</Num>
        <span className={cn("text-sm font-medium", tone === "low" ? "text-low" : "text-ok")}>
          <Num>{formatNumber(lang, Math.round(last.level_percent))}%</Num>
        </span>
        <Num>{formatDateTime(lang, last.recorded_at)}</Num>
      </div>

      {last.temperature_celsius !== undefined ? (
        <p className="text-xs text-faint">
          {t("monitoring.temperature")}: <Num>{formatNumber(lang, last.temperature_celsius, 1)}°C</Num>
        </p>
      ) : null}
    </div>
  );
}

function GeneratorTrendCard({ generator }: { generator: Generator }) {
  const { t, lang } = useI18n();
  const readingsQuery = useGeneratorReadings(generator.id);

  if (readingsQuery.isPending) return <LoadingRows rows={2} />;
  if (readingsQuery.error) {
    return <ErrorState error={readingsQuery.error} onRetry={() => void readingsQuery.refetch()} />;
  }

  const points = readingsQuery.data?.points ?? [];
  if (points.length === 0) {
    return (
      <EmptyState
        icon={<Radio className="size-6" />}
        title={t("monitoring.noReadingsTitle")}
        body={t("monitoring.noReadingsGeneratorBody")}
      />
    );
  }

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const chartPoints = points.map((p) => ({ x: new Date(p.recorded_at).getTime(), y: p.running_hours }));

  return (
    <div className="flex flex-col gap-3">
      <ChartFrame
        label={`${t("monitoring.generatorTrendTitle")} — ${generator.name}, ${t(
          "monitoring.chartSummary",
          {
            from: `${formatHours(lang, first.running_hours)} ${t("common.hours")}`,
            to: `${formatHours(lang, last.running_hours)} ${t("common.hours")}`,
          },
        )}`}
      >
        <TrendChart points={chartPoints} tone="idle" />
      </ChartFrame>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-faint">
        <Num>{formatDateTime(lang, first.recorded_at)}</Num>
        <span className="text-sm font-medium text-text">
          <Num>{formatHours(lang, last.running_hours)}</Num> {t("common.hours")}
        </span>
        <Num>{formatDateTime(lang, last.recorded_at)}</Num>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ page */

export function MonitoringPage() {
  const { t } = useI18n();

  const sitesQuery = useSites({ status: "active", limit: 100 });
  const sites = useMemo(() => sitesQuery.data?.data ?? [], [sitesQuery.data]);
  const siteIds = useMemo(() => sites.map((s) => s.id), [sites]);
  const siteName = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  const tanksQuery = useAllTanks(siteIds);
  const generatorsQuery = useAllGenerators(siteIds);
  const orgQuery = useOrganization();

  const activeTanks = useMemo(
    () => tanksQuery.tanks.filter((tank) => tank.status === "active"),
    [tanksQuery.tanks],
  );
  const activeGenerators = useMemo(
    () => generatorsQuery.generators.filter((g) => g.status !== "decommissioned"),
    [generatorsQuery.generators],
  );

  const [selectedTankId, setSelectedTankId] = useState<string>(NONE);
  const [selectedGeneratorId, setSelectedGeneratorId] = useState<string>(NONE);

  // Default to the first tank/generator once the fleet has loaded, so the trend section is
  // never empty just because nobody has picked one yet.
  const tankId = selectedTankId !== NONE ? selectedTankId : (activeTanks[0]?.id ?? NONE);
  const generatorId =
    selectedGeneratorId !== NONE ? selectedGeneratorId : (activeGenerators[0]?.id ?? NONE);

  const selectedTank = activeTanks.find((tank) => tank.id === tankId);
  const selectedGenerator = activeGenerators.find((g) => g.id === generatorId);

  const isPending =
    sitesQuery.isPending || tanksQuery.isPending || generatorsQuery.isPending || orgQuery.isPending;
  const error = sitesQuery.error ?? tanksQuery.error ?? generatorsQuery.error ?? orgQuery.error;

  const orgThreshold = orgQuery.data?.settings.low_fuel_threshold_percent ?? 20;

  if (error) return <ErrorState error={error} onRetry={() => void sitesQuery.refetch()} />;

  return (
    <>
      <PageHeader title={t("monitoring.title")} description={t("monitoring.subtitle")} />

      {/* ══ LIVE TANKS ══════════════════════════════════════════════════════ */}
      <section aria-labelledby="live-tanks" className="mb-8">
        <h2 id="live-tanks" className="mb-3 text-sm font-semibold text-text weight-display">
          {t("monitoring.tanksSection")}
        </h2>

        {isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Card key={i}>
                <LoadingRows rows={2} />
              </Card>
            ))}
          </div>
        ) : activeTanks.length === 0 ? (
          <Card>
            <EmptyState icon={<Radio className="size-6" />} title={t("monitoring.tanksEmpty")} />
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeTanks.map((tank) => (
              <TankTile
                key={tank.id}
                tank={tank}
                siteName={siteName.get(tank.site_id) ?? ""}
                orgThreshold={orgThreshold}
              />
            ))}
          </div>
        )}
      </section>

      {/* ══ LIVE GENERATORS ═══════════════════════════════════════════════ */}
      <section aria-labelledby="live-generators" className="mb-8">
        <h2 id="live-generators" className="mb-3 text-sm font-semibold text-text weight-display">
          {t("monitoring.generatorsSection")}
        </h2>

        {isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Card key={i}>
                <LoadingRows rows={2} />
              </Card>
            ))}
          </div>
        ) : activeGenerators.length === 0 ? (
          <Card>
            <EmptyState icon={<Radio className="size-6" />} title={t("monitoring.generatorsEmpty")} />
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeGenerators.map((generator) => (
              <GeneratorTile
                key={generator.id}
                generator={generator}
                siteName={siteName.get(generator.site_id) ?? ""}
              />
            ))}
          </div>
        )}
      </section>

      {/* ══ TRENDS ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="trend" className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-wrap gap-3">
            <CardTitle>{t("monitoring.tankTrendTitle")}</CardTitle>
            {activeTanks.length > 0 ? (
              <div className="w-full max-w-56 sm:w-56">
                <Select
                  value={tankId}
                  onValueChange={setSelectedTankId}
                  options={activeTanks.map((tank) => ({
                    value: tank.id,
                    label: `${siteName.get(tank.site_id) ?? ""} — ${tank.name}`,
                  }))}
                  aria-label={t("monitoring.selectTank")}
                />
              </div>
            ) : null}
          </CardHeader>
          <CardBody>
            {isPending ? (
              <LoadingRows rows={2} />
            ) : selectedTank ? (
              <TankTrendCard tank={selectedTank} orgThreshold={orgThreshold} />
            ) : (
              <EmptyState title={t("monitoring.noTankSelected")} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex-wrap gap-3">
            <CardTitle>{t("monitoring.generatorTrendTitle")}</CardTitle>
            {activeGenerators.length > 0 ? (
              <div className="w-full max-w-56 sm:w-56">
                <Select
                  value={generatorId}
                  onValueChange={setSelectedGeneratorId}
                  options={activeGenerators.map((generator) => ({
                    value: generator.id,
                    label: `${siteName.get(generator.site_id) ?? ""} — ${generator.name}`,
                  }))}
                  aria-label={t("monitoring.selectGenerator")}
                />
              </div>
            ) : null}
          </CardHeader>
          <CardBody>
            {isPending ? (
              <LoadingRows rows={2} />
            ) : selectedGenerator ? (
              <GeneratorTrendCard generator={selectedGenerator} />
            ) : (
              <EmptyState title={t("monitoring.noGeneratorSelected")} />
            )}
          </CardBody>
        </Card>
      </section>
    </>
  );
}
